/**
 * Key Result name templates — sourced from KR_Library.xlsx (32 rows).
 * `categoryCode` must match `okr_categories.category_code` (CNDP, CLBD, BPRC, SOBR, LRNG)
 * or be inferable from the category name (see `inferCategoryCodeFromCategoryName`)
 * for databases where `category_code` was not backfilled.
 */

export const TEMPLATE_TIER = {
  primary: 'primary',
  secondary: 'secondary'
};

/**
 * @typedef {{ id: string, categoryCode: string, name: string, description: string, priority: 'primary'|'secondary', krType?: 'Activity'|'Outcome', baseFrequency?: string }} OkrKeyResultNameTemplate
 */

/** @type {OkrKeyResultNameTemplate[]} */
export const OKR_KEY_RESULT_NAME_TEMPLATES = [
  {
    id: "BPRC-LIB-001",
    categoryCode: "BPRC",
    name: "Bench Candidates Handled",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Total number of bench consultants actively managed in the pipeline \u2014 including follow-ups, status updates, marketing submissions, and availability checks. Employee directly controls this through daily outreach effort. Measured weekly because bench pipeline is reviewed on a weekly rhythm.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-002",
    categoryCode: "BPRC",
    name: "Bench Candidates Brought (New)",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of new bench consultants added to the pipeline this week \u2014 sourced from LinkedIn, referrals, job boards, or direct outreach. Employee controls sourcing activity. Measured weekly because new additions are tracked on a weekly basis against pipeline growth targets.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-003",
    categoryCode: "BPRC",
    name: "Bench Closures / Placements",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of bench consultants successfully placed with a client and contract executed. This is an OUTCOME KR \u2014 the result depends on client decisions, interview outcomes, rate negotiations, and timing, none of which the employee fully controls on a daily basis. A deal cycle typically spans 2\u20136 weeks so monthly is the correct scoring period. Shown as In Progress during weekly reviews.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-004",
    categoryCode: "CNDP",
    name: "New Candidates Sourced",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Count. Base frequency: Daily; scoring: Weekly. Number of new candidates identified and added to the ATS/CRM \u2014 from LinkedIn, job boards, referrals, or direct outreach. Employee has full control over sourcing volume every day. Base is daily because sourcing should happen continuously. Scored weekly as a rollup of daily activity.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-005",
    categoryCode: "CNDP",
    name: "Submissions to Client",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of candidate profiles formally submitted to a client for an open requirement. Controlled by the recruiter \u2014 depends on having qualified candidates and open requirements to submit against. Measured weekly because submission cycles align with client feedback loops which are typically weekly.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-006",
    categoryCode: "CNDP",
    name: "Interviews Scheduled",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of interviews confirmed between a candidate and a client. Partially within employee control \u2014 requires both candidate availability and client scheduling. Measured weekly because interview scheduling is a weekly activity in the recruiting cycle.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-007",
    categoryCode: "CNDP",
    name: "Candidate Contracts Received / Closed",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of candidate placement contracts received and executed \u2014 meaning the candidate has been offered, accepted, and the contract is signed. This is an OUTCOME KR \u2014 it depends on client offer decisions, candidate acceptance, background checks, and start date confirmation. The full cycle from interview to contract typically takes 2\u20134 weeks, making monthly the correct scoring frequency. Shown as In Progress during weekly reviews.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-008",
    categoryCode: "CLBD",
    name: "New Requirements Received",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of new job requirements / open positions received from clients this week. While client decision to send a requirement depends on their needs, the BD person's consistent outreach and relationship-building directly drives this. Measured weekly because requirement intake follows a weekly client engagement rhythm.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-009",
    categoryCode: "CLBD",
    name: "New Accounts Created",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of new client company accounts opened in the CRM this week \u2014 representing a new business relationship initiated. Employee controls prospecting and outreach. Measured weekly because BD activity targets are set weekly.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-010",
    categoryCode: "CLBD",
    name: "New Contacts Added",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of new individual contacts (hiring managers, VPs, procurement) added to the CRM this week. Fully within employee control through calls, LinkedIn, and networking. Measured weekly to track relationship-building velocity.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-011",
    categoryCode: "CLBD",
    name: "Client Contracts Signed",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of new client agreements / MSAs (Master Service Agreements) signed this month. This is an OUTCOME KR \u2014 requires legal review, procurement approval, rate negotiation, and executive sign-off on the client side, none of which the employee controls directly. Sales cycles typically span 4\u201312 weeks. Scored monthly and shown as In Progress during weekly reviews.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-012",
    categoryCode: "CLBD",
    name: "Requirement Closures / Placements",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of client requirements successfully fulfilled \u2014 candidate placed and started. This is an OUTCOME KR because it depends on both the recruiter delivering a qualified candidate AND the client making a hiring decision. The combined cycle from requirement receipt to placement start is typically 3\u20138 weeks, making monthly the correct scoring period.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-013",
    categoryCode: "CNDP",
    name: "Calls Made",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Count. Base frequency: Daily; scoring: Weekly. Total number of outbound calls made to candidates, clients, or prospects. Fully within employee control \u2014 requires no external decision. Base is daily because calls should happen every single working day. Scored weekly as a rollup of daily call logs from the CRM or phone system.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-014",
    categoryCode: "CNDP",
    name: "Chats Conducted (Platform)",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Count. Base frequency: Daily; scoring: Weekly. Number of chat conversations initiated or responded to on internal/external platforms (Teams, Slack, portal chats). Fully within employee control. Base is daily because messaging is a continuous daily activity. Scored weekly as a rollup.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CNDP-LIB-015",
    categoryCode: "CNDP",
    name: "WhatsApp Chats Sent",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Count. Base frequency: Daily; scoring: Weekly. Number of WhatsApp messages sent to candidates, consultants, or clients. A primary communication channel in staffing for quick outreach. Fully within employee control daily. Base is daily, scored weekly as a rollup of daily totals.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "CLBD-LIB-016",
    categoryCode: "CLBD",
    name: "Activities Logged in CRM",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Count. Base frequency: Daily; scoring: Weekly. Number of any activity recorded in the CRM \u2014 calls logged, emails sent, notes added, follow-ups set, status updates made. A proxy for how diligently the employee is documenting their work. Fully within employee control every day. Base is daily, scored weekly. Also used to validate other activity KRs.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-017",
    categoryCode: "BPRC",
    name: "Tickets Handled",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of internal or external support/service tickets resolved this week \u2014 including consultant queries, client requests, document requests, and HR issues. Volume depends partly on inbound requests but handling speed is within employee control. Measured weekly because ticket queues are managed on a weekly basis.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-018",
    categoryCode: "BPRC",
    name: "Punched-In Hours Logged",
    krType: "Activity",
    baseFrequency: "Daily",
    description: "Unit: Hours. Base frequency: Daily; scoring: Weekly. Total hours logged in the time tracking / attendance system per week. Confirms the employee was actively working and present. Fully within employee control. Base is daily \u2014 expected to log every working day. Scored weekly as a sum of daily hours. Minimum expected is typically 8 hours per day, 40 hours per week.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-019",
    categoryCode: "BPRC",
    name: "H1B Cases Handled",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of H1B visa petitions or transfer cases processed to completion this month \u2014 including LCA filing, petition preparation, and USCIS submission. This is an OUTCOME KR because case volume depends on client hiring needs and candidate situations, not daily effort alone. Processing timelines span weeks. Measured monthly to align with USCIS processing cycles and case intake volume.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-020",
    categoryCode: "BPRC",
    name: "GC Cases Handled",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of Green Card (employment-based) cases processed this month \u2014 PERM labor certification, I-140, I-485 stages. This is an OUTCOME KR \u2014 GC cases have multi-stage processes spanning months to years. Monthly measurement captures case progression milestones, not final approvals. Each stage completed counts as a case handled.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "BPRC-LIB-021",
    categoryCode: "BPRC",
    name: "E-Verify Completions",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of E-Verify employment eligibility verifications completed for new hires or contractors. Triggered by each new placement start. Employee controls timely processing \u2014 federal law requires completion within 3 business days of start date. Measured weekly because placements start throughout the week and compliance is time-sensitive.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-022",
    categoryCode: "SOBR",
    name: "LinkedIn Posts",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of posts published on LinkedIn from the employee's personal or company account \u2014 job postings, thought leadership articles, candidate success stories, company updates. Fully within employee control. Measured weekly because LinkedIn content follows a weekly publishing calendar. LinkedIn is the primary professional network for staffing and BD outreach.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-023",
    categoryCode: "SOBR",
    name: "Facebook Posts",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of posts published on Facebook \u2014 job openings, company culture posts, hiring events. Measured weekly on a content calendar basis. Supports brand visibility and candidate reach in communities where Facebook remains a primary platform.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-024",
    categoryCode: "SOBR",
    name: "Twitter / X Posts",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of tweets/posts on Twitter/X \u2014 industry news, job alerts, company updates, engagement with hiring community. Measured weekly on content calendar. Supports real-time brand visibility and thought leadership in the tech and staffing space.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-025",
    categoryCode: "SOBR",
    name: "Instagram Posts",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of posts on Instagram \u2014 employer brand content, team culture, office life, candidate testimonials. Measured weekly. Primarily used for employer branding targeting younger candidate demographics and showcasing company culture visually.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-026",
    categoryCode: "SOBR",
    name: "YouTube Videos / Posts",
    krType: "Activity",
    baseFrequency: "Monthly",
    description: "Unit: Count. Base frequency: Monthly; scoring: Monthly. Number of videos published on the company YouTube channel \u2014 interview tips, career advice, company culture videos, success stories, job fairs. Measured monthly because video production requires scripting, recording, editing, and publishing \u2014 a multi-day effort that does not lend itself to weekly targets. One quality video per month is a realistic expectation.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "SOBR-LIB-027",
    categoryCode: "SOBR",
    name: "TikTok Posts",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of short-form videos posted on TikTok \u2014 behind the scenes, job tips, company culture, recruiter day-in-the-life. Measured weekly. TikTok targets a younger demographic and supports brand visibility in a growing social channel for employer branding and candidate attraction.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "LRNG-LIB-028",
    categoryCode: "LRNG",
    name: "Training Hours Attended",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Hours. Base frequency: Weekly; scoring: Weekly. Number of hours spent in formal training sessions \u2014 onboarding modules, compliance training, sales skills, immigration law updates, platform training, leadership development. Fully within employee control by attending scheduled sessions. Measured weekly because training is typically scheduled in weekly blocks. Higher weight for junior levels to reinforce skill development as a core expectation.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "LRNG-LIB-029",
    categoryCode: "LRNG",
    name: "Meetings Attended",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of team meetings, client calls, one-on-ones, or company-wide meetings attended and participated in. Fully within employee control. Measured weekly because meetings are scheduled on a weekly basis. Tracks collaboration, team alignment, and communication engagement. Includes both internal team meetings and external client or candidate meetings.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "LRNG-LIB-030",
    categoryCode: "LRNG",
    name: "Team Average OKR Score",
    krType: "Outcome",
    baseFrequency: "Weekly",
    description: "Unit: Score. Base frequency: Weekly; scoring: Weekly. The average Final OKR Score across all direct reports for the current week. This is an OUTCOME KR for the manager \u2014 it reflects the collective performance of the team they lead. Managers cannot directly control individual employee scores but are accountable for coaching, enabling, and removing blockers. Measured weekly because OKR scores are calculated weekly.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "LRNG-LIB-031",
    categoryCode: "LRNG",
    name: "Team Placement Target % Achieved",
    krType: "Outcome",
    baseFrequency: "Monthly",
    description: "Unit: Percent. Base frequency: Monthly; scoring: Monthly. Percentage of the team's monthly placement target that has been achieved \u2014 total placements made divided by total placements targeted. This is an OUTCOME KR \u2014 depends on the entire team's combined performance against their individual targets. Measured monthly because placement targets are set monthly and results accumulate over the full month.",
    priority: TEMPLATE_TIER.primary
  },
  {
    id: "LRNG-LIB-032",
    categoryCode: "LRNG",
    name: "Team Members Coached This Week",
    krType: "Activity",
    baseFrequency: "Weekly",
    description: "Unit: Count. Base frequency: Weekly; scoring: Weekly. Number of direct reports who received a formal one-on-one coaching session, performance feedback, or structured development conversation this week. Fully within manager's control \u2014 they schedule and conduct coaching sessions. Measured weekly to ensure consistent coaching cadence. Tied to team development and retention outcomes.",
    priority: TEMPLATE_TIER.primary
  }
];

let okrTemplateLibraryRuntime = [...OKR_KEY_RESULT_NAME_TEMPLATES];

export function setOkrTemplateLibrary(templates) {
  if (!Array.isArray(templates) || templates.length === 0) {
    okrTemplateLibraryRuntime = [...OKR_KEY_RESULT_NAME_TEMPLATES];
    return;
  }
  const localById = new Map(OKR_KEY_RESULT_NAME_TEMPLATES.map((x) => [String(x.id), x]));
  okrTemplateLibraryRuntime = templates.map((t) => {
    const local = localById.get(String(t.id)) || {};
    return {
      id: t.id,
      categoryCode: t.categoryCode,
      name: t.name,
      description: t.description || '',
      priority: t.priority || local.priority || TEMPLATE_TIER.primary,
      krType: t.krType || local.krType || null,
      baseFrequency: t.baseFrequency || local.baseFrequency || null
    };
  });
}

export function getOkrTemplateLibrary() {
  return okrTemplateLibraryRuntime;
}

const KNOWN_TEMPLATE_CATEGORY_CODES = new Set(['CNDP', 'CLBD', 'BPRC', 'SOBR', 'LRNG']);

/**
 * Maps a seeded OKR category display name to its 4-letter code when `category_code` is missing.
 * Align with `okrSeedCategories` in erp4c-base-backend/src/config/seed.js.
 * @param {string} [name]
 * @returns {string|null}
 */
export function inferCategoryCodeFromCategoryName(name) {
  if (!name || typeof name !== 'string') return null;
  const s = name.toLowerCase();
  if (s.includes('candidate sourcing')) return 'CNDP';
  if (s.includes('candidate') && s.includes('pipeline')) return 'CNDP';
  if (s.includes('client') && s.includes('biz') && s.includes('dev')) return 'CLBD';
  if (s.includes('client') && s.includes('business') && s.includes('development')) return 'CLBD';
  if (s.includes('social media') && s.includes('brand')) return 'SOBR';
  if (s.includes('social') && s.includes('brand')) return 'SOBR';
  if (s.includes('learning') && s.includes('collab')) return 'LRNG';
  if (s.includes('team performance')) return 'LRNG';
  if (s.includes('bench') && s.includes('pipeline')) return 'BPRC';
  if (s.includes('compliance') && s.includes('immigration')) return 'BPRC';
  if (s.includes('procurement') && s.includes('closure')) return 'BPRC';
  return null;
}

function normalizeCategoryCode(raw) {
  if (raw == null) return '';
  return String(raw).trim().toUpperCase();
}

/**
 * Resolve a category into one of the template library codes.
 * - Uses `category_code` when it is one of known library codes.
 * - Falls back to category name inference (handles random/generated category_code values).
 * @param {any} category
 * @returns {string|null}
 */
export function templateCategoryCodeForCategory(category) {
  if (!category) return null;
  const raw = normalizeCategoryCode(category.category_code);
  if (raw && KNOWN_TEMPLATE_CATEGORY_CODES.has(raw)) return raw;
  const inferred = inferCategoryCodeFromCategoryName(category.name);
  if (inferred) return inferred;
  return null;
}

/** @param {any} [template] */
export function resolveOkrCategoryForTemplate(categories, template) {
  const t = template;
  if (!t?.categoryCode) return null;
  const list = (categories || []).filter((c) => c && c.is_active !== false);
  const want = normalizeCategoryCode(t.categoryCode);
  return list.find((c) => templateCategoryCodeForCategory(c) === want) || null;
}

/**
 * When several OKR categories map to the same template library code (e.g. a seeded “SOBR” row and a
 * custom category whose name infers to SOBR), prefer the category that already holds the most SYST
 * catalog lines so new template adds land next to auto-seeded rows.
 *
 * @param {OkrKeyResultNameTemplate} t
 * @param {any[]} categories
 * @param {any[]} [keyResults]
 * @returns {any|null}
 */
export function pickCategoryForNewTemplateCatalogRow(t, categories, keyResults) {
  if (!t?.categoryCode) return null;
  const want = normalizeCategoryCode(t.categoryCode);
  const sorted = sortCategoriesForTemplateList(null, categories || []);
  const matches = sorted.filter((c) => templateCategoryCodeForCategory(c) === want);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  const krs = keyResults || [];
  let best = matches[0];
  let bestCount = -1;
  for (const c of matches) {
    const n = krs.filter(
      (kr) => String(kr.category_id) === String(c.id) && catalogLineKindUpper(kr) === 'SYST'
    ).length;
    if (n > bestCount) {
      bestCount = n;
      best = c;
    }
  }
  return best;
}

/**
 * Resolve a catalog row for a built-in template across every active category that shares the same
 * library family code as `t.categoryCode`. Use this for KR Id display and “in catalog” checks when
 * `resolveOkrCategoryForTemplate` alone is ambiguous (first match may not be the category that holds
 * seeded SYST lines).
 *
 * @param {OkrKeyResultNameTemplate} t
 * @param {any[]} keyResults
 * @param {any[]} categories
 * @param {{ includeNameMatchCUST?: boolean }} [opts]
 * @returns {any|null}
 */
export function findCatalogKeyResultForTemplateInLibraryFamily(t, keyResults, categories, opts = {}) {
  const { includeNameMatchCUST = true } = opts;
  if (!t?.categoryCode) return null;
  const want = normalizeCategoryCode(t.categoryCode);
  const sorted = sortCategoriesForTemplateList(null, categories || []);
  const matches = sorted.filter((c) => c && c.is_active !== false && templateCategoryCodeForCategory(c) === want);
  if (matches.length === 0) return null;
  const catIds = new Set(matches.map((c) => String(c.id)));
  const krs = keyResults || [];
  const tid = String(t.id);
  const order = new Map(matches.map((c, i) => [String(c.id), i]));
  const bySource = krs
    .filter(
      (kr) =>
        catIds.has(String(kr.category_id)) &&
        String(kr.sourceTemplateId || kr.source_template_id || '') === tid
    )
    .sort(
      (a, b) =>
        (order.get(String(a.category_id)) ?? 99) - (order.get(String(b.category_id)) ?? 99) ||
        String(a.id || '').localeCompare(String(b.id || ''))
    );
  if (bySource.length > 0) return bySource[0];
  const nameTrim = String(t.name).trim();
  const allNames = krs.filter(
    (kr) => catIds.has(String(kr.category_id)) && String((kr.name || '').trim()) === nameTrim
  );
  if (allNames.length === 0) return null;
  const nonCustom = allNames.find((kr) => !isCustomCatalogLine(kr, categories));
  if (nonCustom) return nonCustom;
  if (includeNameMatchCUST) return allNames[0];
  return null;
}

/**
 * Map a name-template row to a catalog line for UI:
 * 1) Prefer the row created from this template (`source_template_id` === t.id)
 * 2) Else same category + name, preferring a library/system line over a custom duplicate with the same name
 * 3) If `includeNameMatchCUST` and still none, a same-name CUST line (so KR Id can show a real code when only a custom exists)
 * @param {OkrKeyResultNameTemplate} t
 * @param {any[]} keyResults
 * @param {any} category
 * @param {any[]} categories
 * @param {{ includeNameMatchCUST?: boolean }} [opts]
 * @returns {any|null}
 */
export function findCatalogKeyResultForTemplate(t, keyResults, category, categories, opts = {}) {
  const { includeNameMatchCUST = true } = opts;
  if (!t || !category) return null;
  const krs = keyResults || [];
  const fromTemplate = krs.find(
    (kr) => String(kr.sourceTemplateId || kr.source_template_id || '') === String(t.id)
  );
  if (fromTemplate) return fromTemplate;
  const nameMatch = (kr) =>
    String(kr.category_id) === String(category.id) && String((kr.name || '').trim()) === String(t.name).trim();
  const allNames = krs.filter(nameMatch);
  if (allNames.length === 0) return null;
  const nonCustom = allNames.find((kr) => !isCustomCatalogLine(kr, categories));
  if (nonCustom) return nonCustom;
  if (includeNameMatchCUST) return allNames[0];
  return null;
}

/** @param {any} targetRow */
export function targetRowJobRoleId(targetRow) {
  if (!targetRow) return null;
  return targetRow.employee_job_role_id ?? targetRow.employeeJobRoleId ?? targetRow.employeeJobRole?.id ?? null;
}

/** @param {any} targetRow */
export function targetRowTitleId(targetRow) {
  if (!targetRow) return null;
  return targetRow.employee_title_id ?? targetRow.employeeTitleId ?? targetRow.employeeTitle?.id ?? null;
}

/**
 * True if the catalog key result has at least one level target for this job role (i.e. it appears in this tab’s working matrix).
 * Use this for Add/Remove; handles mixed snake_case / camelCase from the API.
 * @param {any} catalogKr
 * @param {string|number|undefined|null} jobRoleId
 */
export function catalogRowHasTargetForJobRole(catalogKr, jobRoleId) {
  if (!catalogKr || jobRoleId == null || jobRoleId === '') return false;
  const rid = String(jobRoleId);
  return (catalogKr.targets || []).some((x) => {
    const tr = targetRowJobRoleId(x);
    return tr != null && String(tr) === rid;
  });
}

/**
 * @param {any} catalogKr
 * @returns {string}
 */
export function catalogKeyResultCodeString(catalogKr) {
  if (!catalogKr) return '';
  return String(
    catalogKr.kr_code != null
      ? catalogKr.kr_code
      : catalogKr.krCode != null
        ? catalogKr.krCode
        : ''
  ).trim();
}

/** Active OKR category codes from API (normalized). */
export function categoryCodesFromApi(categories) {
  const codes = new Set();
  for (const c of categories || []) {
    if (!c || c.is_active === false) continue;
    const code = templateCategoryCodeForCategory(c);
    if (code) codes.add(code);
  }
  return codes;
}

/** Only templates for categories the org has, matching `categoryCode` on the row (or name-inferred). */
export function filterTemplatesToExistingCategories(categories) {
  const codes = categoryCodesFromApi(categories);
  return getOkrTemplateLibrary().filter((t) => codes.has(normalizeCategoryCode(t.categoryCode)));
}

export function sortCategoriesForTemplateList(_unused, categories) {
  return [...(categories || [])]
    .filter((c) => c.is_active !== false && templateCategoryCodeForCategory(c))
    .sort(
      (x, y) =>
        (x.sort_order || 0) - (y.sort_order || 0) ||
        String(x.name || '').localeCompare(String(y.name || ''), undefined, { sensitivity: 'base' })
    );
}

/** SYST / CUST / '' for legacy rows before migration */
export function catalogLineKindUpper(catalogKr) {
  return String(catalogKr?.catalogLineKind || catalogKr?.catalog_line_kind || '').toUpperCase();
}

/**
 * True if this catalog row originated from (or matches) the built-in name template library.
 * Uses `catalog_line_kind` when set (SYST); CUST is never a library row.
 * Otherwise uses `source_template_id` or name + resolved category code (legacy).
 * @param {any} catalogKr
 * @param {any[]} [categories]
 */
export function isCatalogRowFromLibraryTemplate(catalogKr, categories) {
  if (!catalogKr) return false;
  const kind = catalogLineKindUpper(catalogKr);
  if (kind === 'SYST') return true;
  if (kind === 'CUST') return false;
  if (catalogKr.sourceTemplateId || catalogKr.source_template_id) return true;
  const cat =
    catalogKr.category ||
    (categories || []).find((c) => c && String(c.id) === String(catalogKr.category_id));
  const code = templateCategoryCodeForCategory(cat);
  if (!code) return false;
  const name = String(catalogKr.name || '').trim();
  return getOkrTemplateLibrary().some(
    (tm) => normalizeCategoryCode(tm.categoryCode) === code && String(tm.name).trim() === name
  );
}

/** Custom catalog lines only (CUST, or legacy rows that are not library templates). */
export function isCustomCatalogLine(catalogKr, categories) {
  if (!catalogKr) return false;
  const kind = catalogLineKindUpper(catalogKr);
  if (kind === 'CUST') return true;
  if (kind === 'SYST') return false;
  return !isCatalogRowFromLibraryTemplate(catalogKr, categories);
}
